import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
} from '@mui/material';
import LocationSearchInput from '../../Stateless/LocationSearchInput';
import { useAllDeliveryBoyMap } from '../../Query/DeliveryBoyMapQuery';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import deliveryBikeIcon from '../../../assets/delivery-bike.png';

// Helper to create a badge overlay
function createBadgeOverlay(map: google.maps.Map, position: google.maps.LatLng, count: number) {
    // Create a div for the badge
    const badgeDiv = document.createElement('div');
    badgeDiv.style.position = 'absolute';
    badgeDiv.style.width = '16px';
    badgeDiv.style.height = '16px';
    badgeDiv.style.background = '#d32f2f'; // Material-UI error color
    badgeDiv.style.color = '#fff';
    badgeDiv.style.fontWeight = 'bold';
    badgeDiv.style.fontSize = '10px';
    badgeDiv.style.borderRadius = '50%';
    badgeDiv.style.display = 'flex';
    badgeDiv.style.alignItems = 'center';
    badgeDiv.style.justifyContent = 'center';
    badgeDiv.style.border = '2px solid #fff';
    badgeDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
    badgeDiv.style.zIndex = '1000';
    badgeDiv.innerText = String(count);
    // Pulse animation
    badgeDiv.style.animation = 'badgePulse 1.5s infinite';
    // Add keyframes for pulse animation
    if (!document.getElementById('badgePulseKeyframes')) {
        const style = document.createElement('style');
        style.id = 'badgePulseKeyframes';
        style.innerHTML = `@keyframes badgePulse {
            0% { box-shadow: 0 0 0 0 rgba(211,47,47, 0.7); }
            70% { box-shadow: 0 0 0 6px rgba(211,47,47, 0); }
            100% { box-shadow: 0 0 0 0 rgba(211,47,47, 0); }
        }`;
        document.head.appendChild(style);
    }

    // Custom OverlayView
    function BadgeOverlay(this: google.maps.OverlayView & { div: HTMLDivElement; position: google.maps.LatLng; map: google.maps.Map }, position: google.maps.LatLng, map: google.maps.Map) {
        this.position = position;
        this.map = map;
        this.div = badgeDiv;
        this.setMap(map);
    }
    BadgeOverlay.prototype = Object.create(window.google.maps.OverlayView.prototype);
    BadgeOverlay.prototype.onAdd = function (this: google.maps.OverlayView & { div: HTMLDivElement }) {
        const panes = this.getPanes();
        if (panes && panes.overlayMouseTarget) {
            panes.overlayMouseTarget.appendChild(this.div);
        }
    };
    BadgeOverlay.prototype.draw = function (this: google.maps.OverlayView & { div: HTMLDivElement; position: google.maps.LatLng }) {
        const projection = this.getProjection();
        const pos = projection.fromLatLngToDivPixel(this.position);
        if (pos) {
            // Place badge exactly at the head (top) of the marker
            const badgeWidth = 20;
            const badgeHeight = 16;
            const markerIconHeight = 60;
            this.div.style.left = `${pos.x - badgeWidth/4}px`;
            this.div.style.top = `${pos.y - badgeHeight - markerIconHeight/2}px`;
        }
    };
    BadgeOverlay.prototype.onRemove = function (this: google.maps.OverlayView & { div: HTMLDivElement }) {
        if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
        }
    };
    return new (BadgeOverlay as any)(position, map);
}

const DeliveryBoyMap = () => {
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [mobileNo, setMobileNo] = useState('');
    const [location, setLocation] = useState('');
    const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [locationLat, setLocationLat] = useState<number | null>(null);
    const [locationLng, setLocationLng] = useState<number | null>(null);
    const [searchParams, setSearchParams] = useState<any>({});
    const [validationError, setValidationError] = useState<string>('');
    const [isMapLoading, setIsMapLoading] = useState(false);

    const handleSetAddress: React.Dispatch<React.SetStateAction<string>> = (value) => {
        setLocation(value);
        setValidationError('');
    };

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const initializedRef = useRef(false);
    const badgeOverlaysRef = useRef<any[]>([]);

    const { showMessage } = useSnackbar();

    const { data: driverData, isLoading: driverDataMapLoading, refetch } = useAllDeliveryBoyMap(searchParams);
    const drivers = driverData || [];

    let filteredDrivers = drivers;
    if (searchParams.name) {
        filteredDrivers = filteredDrivers.filter((d: any) =>
            d.name === searchParams.name
        );
    }
    if (mobileNo) {
        filteredDrivers = filteredDrivers.filter((d: any) =>
            d.mobileNumber?.includes(mobileNo)
        );
    }
    filteredDrivers = filteredDrivers.filter(
        (d: any) => d.coordinates?.latitude && d.coordinates?.longitude
    );

    useEffect(() => {
        const interval = setInterval(() => {
            refetch();
        }, 60000);
        return () => clearInterval(interval);
    }, [refetch]);

    // Initialize map
    useEffect(() => {
        if (!mapRef.current || !window.google) return;
        if (!initializedRef.current) {
            mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                center: { lat: 20, lng: 0 }, // World center
                zoom: 2, // World view zoom level
                streetViewControl: false,
                mapTypeControl: true,
                fullscreenControl: true,
                minZoom: 1, // Allow zooming out to world view
                maxZoom: 20,
                gestureHandling: 'cooperative',
                zoomControl: true,
                scaleControl: true,
            });
            initializedRef.current = true;
        }
    }, []);

    const groupDriversByLocation = (drivers: any[]) => {
        const groups: { [key: string]: any[] } = {};
        const processedDrivers = new Set();

        drivers.forEach((driver, index) => {
            if (processedDrivers.has(index)) return;

            const lat = Number(driver.coordinates?.latitude);
            const lng = Number(driver.coordinates?.longitude);
            if (!lat || !lng) return;

            const groupKey = `group_${index}`;
            groups[groupKey] = [driver];
            processedDrivers.add(index);

            const radius = 0.001; // Increased radius to catch nearby drivers

            drivers.forEach((otherDriver, otherIndex) => {
                if (index === otherIndex || processedDrivers.has(otherIndex)) return;

                const otherLat = Number(otherDriver.coordinates?.latitude);
                const otherLng = Number(otherDriver.coordinates?.longitude);
                if (!otherLat || !otherLng) return;

                const distance = Math.sqrt(
                    Math.pow(lat - otherLat, 2) + Math.pow(lng - otherLng, 2)
                );

                if (distance <= radius) {
                    groups[groupKey].push(otherDriver);
                    processedDrivers.add(otherIndex);
                }
            });
        });

        return groups;
    };

    useEffect(() => {
        if (!mapInstanceRef.current || !window.google) return;

        // Remove old markers
        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];

        // Remove old badge overlays
        badgeOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
        badgeOverlaysRef.current = [];

        const locationGroups = groupDriversByLocation(filteredDrivers);

        Object.entries(locationGroups).forEach(([_, driversAtLocation]) => {
            const totalLat = driversAtLocation.reduce((sum, driver) =>
                sum + Number(driver.coordinates?.latitude), 0);
            const totalLng = driversAtLocation.reduce((sum, driver) =>
                sum + Number(driver.coordinates?.longitude), 0);
            const centerLat = totalLat / driversAtLocation.length;
            const centerLng = totalLng / driversAtLocation.length;

            const marker = new window.google.maps.Marker({
                position: { lat: centerLat, lng: centerLng },
                map: mapInstanceRef.current!,
                title: driversAtLocation.length > 1 ? `${driversAtLocation.length} drivers` : driversAtLocation[0].name,
                icon: {
                    url: deliveryBikeIcon, // Local delivery bike image
                    scaledSize: new window.google.maps.Size(40, 40), // Keep size similar to default marker
                    anchor: new window.google.maps.Point(20, 40), // Anchor at bottom center
                },
            });

            let infoContent = `<div style="padding: 8px; max-width: 250px;">`;
            if (driversAtLocation.length > 1) {
                infoContent += `<h4 style="margin: 0 0 8px 0; color: #1976d2;">Multiple Drivers (${driversAtLocation.length})</h4>`;
                driversAtLocation.forEach((d, i) => {
                    infoContent += `
                        <div style="border-bottom: 1px solid #eee; padding: 4px 0; ${i === driversAtLocation.length - 1 ? 'border-bottom: none;' : ''}">
                            <strong>${d.name}</strong><br/>
                            Mobile: ${d.mobileNumber}<br/>
                            Vehicle: ${d.vehicleName}
                        </div>
                    `;
                });
            } else {
                infoContent += `
                    <div>
                        <strong>${driversAtLocation[0].name}</strong><br/>
                        Mobile: ${driversAtLocation[0].mobileNumber}<br/>
                        Vehicle: ${driversAtLocation[0].vehicleName}
                    </div>
                `;
            }
            infoContent += `</div>`;

            const infoWindow = new window.google.maps.InfoWindow({
                content: infoContent,
            });

            marker.addListener('click', () => {
                infoWindow.open(mapInstanceRef.current, marker);
            });

            markersRef.current.push(marker);

            // Add badge overlay for multiple drivers
            if (driversAtLocation.length > 1) {
                const overlay = createBadgeOverlay(
                    mapInstanceRef.current!,
                    new window.google.maps.LatLng(centerLat, centerLng),
                    driversAtLocation.length
                );
                badgeOverlaysRef.current.push(overlay);
            }
        });

        if (locationCoords) {
            mapInstanceRef.current.setCenter(locationCoords);
            mapInstanceRef.current.setZoom(13);
        } else if (filteredDrivers.length > 0) {
            // If we have drivers, fit bounds to show all drivers
            if (filteredDrivers.length > 1) {
                const bounds = new window.google.maps.LatLngBounds();
                filteredDrivers.forEach((driver: any) => {
                    const lat = Number(driver.coordinates.latitude);
                    const lng = Number(driver.coordinates.longitude);
                    if (lat && lng) {
                        bounds.extend({ lat, lng });
                    }
                });
                mapInstanceRef.current.fitBounds(bounds);
                // Add some padding to the bounds
                window.google.maps.event.addListenerOnce(mapInstanceRef.current, 'bounds_changed', () => {
                    if (mapInstanceRef.current) {
                        const currentZoom = mapInstanceRef.current.getZoom();
                        if (currentZoom && currentZoom > 15) {
                            mapInstanceRef.current.setZoom(15);
                        }
                    }
                });
            } else {
                // Single driver - center on that driver
                const lat = Number(filteredDrivers[0].coordinates.latitude);
                const lng = Number(filteredDrivers[0].coordinates.longitude);
                mapInstanceRef.current.setCenter({ lat, lng });
                mapInstanceRef.current.setZoom(12);
            }
        } else {
            // No drivers found - reset to world view
            mapInstanceRef.current.setCenter({ lat: 20, lng: 0 });
            mapInstanceRef.current.setZoom(2);
        }
    }, [filteredDrivers, locationCoords]);

    const handleSearchLocation = ({ latLng, city, state, country }: any) => {
        setSelectedDriverId('');
        setLocationCoords(latLng);
        setLocation(`${city}, ${state}, ${country}`);
        setLocationLat(latLng?.lat ?? null);
        setLocationLng(latLng?.lng ?? null);
    };

    const handleReset = () => {
        setValidationError('');
        setSearchParams({});
        setSelectedDriverId('');
        setMobileNo('');
        setLocation('');
        setLocationCoords(null);
        // Reset map to world view
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter({ lat: 20, lng: 0 });
            mapInstanceRef.current.setZoom(2);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setIsMapLoading(true);
        let error = '';
        if (!selectedDriverId && !location && !mobileNo) {
            error = 'Please select a delivery boy or enter a location.';
        } else if (selectedDriverId && location) {
            error = 'Please select only one filter at a time.';
        } else if (selectedDriverId && !drivers.find((d: any) => String(d.deliveryBoyId) === String(selectedDriverId))) {
            error = 'Selected delivery boy is invalid.';
        } else if (location && location.trim() === '') {
            error = 'Please enter a valid location.';
        } else if (location && (!locationLat || !locationLng)) {
            error = 'Please select a valid location from the suggestions.';
        }
        if (error) {
            setValidationError(error);
            setIsMapLoading(false);
            return;
        }
        setValidationError('');
        if (selectedDriverId) {
            const selectedDriver = drivers.find((d: any) => String(d.deliveryBoyId) === String(selectedDriverId));
            if (selectedDriver && selectedDriver.name) {
                setSearchParams({ name: selectedDriver.name });
            }
            setLocation('');
            setLocationLat(null);
            setLocationLng(null);
            // Do NOT clear setSelectedDriverId here!
            refetch(); // Manually refresh the API
            setTimeout(() => {
                if (driverData && Array.isArray(driverData) && driverData.length === 0) {
                    showMessage('No drivers available', 'info');
                }
            }, 500);
            return;
        }
        // If a location is entered, search by lat/lng only
        if (locationLat && locationLng) {
            setSearchParams({ latitude: locationLat, longitude: locationLng });
            // Message will be handled in useEffect
            return;
        }
        // If neither, clear params
        setSearchParams({});
        refetch(); // Manually refresh the API
        setTimeout(() => {
            if (driverData && Array.isArray(driverData) && driverData.length === 0) {
                showMessage('No drivers available', 'info');
            }
        }, 500);
    };

    // Show 'No drivers available' message for location search after data loads
    useEffect(() => {
        if (
            searchParams.latitude &&
            searchParams.longitude &&
            !driverDataMapLoading &&
            Array.isArray(driverData) &&
            driverData.length === 0
        ) {
            showMessage('No drivers available', 'error');
        }
    }, [driverData, driverDataMapLoading, searchParams, showMessage]);

    // When driverDataMapLoading changes to false, stop map loading
    useEffect(() => {
        if (!driverDataMapLoading) {
            setIsMapLoading(false);
        }
    }, [driverDataMapLoading]);

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
                <Typography sx={{ fontWeight: 500, fontSize: 16, mb: 2 }}>
                    Search Delivery Boy
                </Typography>
                {validationError && (
                    <Typography sx={{ color: 'red', mb: 2, fontSize: 14, fontWeight: 500 }}>
                        {validationError}
                    </Typography>
                )}
                <form onSubmit={handleSearch}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        <Typography>Delivery boy name</Typography>
                        <Box
                            sx={{
                                width: '300px',
                                filter: location ? 'blur(0.8px)' : 'none',
                                pointerEvents: location ? 'none' : 'auto',
                                transition: 'filter 0.2s',
                            }}
                        >
                            <Autocomplete
                                options={drivers.slice(0, 10)}
                                getOptionLabel={(option) => option.name || ''}
                                value={drivers.find((d: any) => String(d.deliveryBoyId) === String(selectedDriverId)) || null}
                                onChange={(_, newValue) => {
                                    setSelectedDriverId(newValue ? newValue.deliveryBoyId : '');
                                    setLocation('');
                                    setLocationCoords(null);
                                    setValidationError('');
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    String(option.deliveryBoyId) === String(value.deliveryBoyId)
                                }
                                openOnFocus
                                clearOnBlur
                                disabled={!!location}
                                sx={{ width: '300px' }}
                                onOpen={() => {
                                    setSearchParams({})
                                }}
                                loading={driverDataMapLoading}
                                noOptionsText={driverDataMapLoading ? '' : 'No options'}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Delivery Boy"
                                        size="small"
                                        sx={{
                                            width: '300px',
                                            '& .MuiInputBase-root': {
                                                width: '229px',
                                                marginTop: '12px',
                                                height: 42,
                                                display: 'flex',
                                                alignItems: 'center',
                                                boxSizing: 'border-box',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                height: '100%',
                                                padding: '0 14px',
                                                boxSizing: 'border-box',
                                            },
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {driverDataMapLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                          />                        
                        </Box>
                        {/* Location search input */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                                minWidth: 300,
                                filter: selectedDriverId ? 'blur(0.8px)' : 'none',
                                pointerEvents: selectedDriverId ? 'none' : 'auto',
                                transition: 'filter 0.2s',
                            }}
                            >
                            <Typography sx={{ mt: '9px', mr: '19px' }}>Location</Typography>
                            <LocationSearchInput
                                handlesearchLocation={handleSearchLocation}
                                address={location}
                                setAddress={handleSetAddress}
                                disabled={!!selectedDriverId}
                            />
                        </Box>
                        <Button type="submit" variant="contained" sx={{ minWidth: 90, height: 42 }}>
                            Search
                        </Button>
                        <Button onClick={handleReset} variant="outlined" disabled={!selectedDriverId && !location}>
                            Reset
                        </Button>
                    </Box>
                </form>

                {/* Optional: Conflict hint */}
                {selectedDriverId && location && (
                    <Typography sx={{ color: 'red', mt: 1 }}>
                        Note: Only one filter (driver or location) is applied at a time. Location has been cleared.
                    </Typography>
                )}
            </Paper>

            {/* Map Section */}
            <Paper variant="outlined" sx={{ height: 600, width: '100%', position: 'relative' }}>
                <Box ref={mapRef} sx={{ height: '100%', width: '100%' }} />
                {isMapLoading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0, left: 0, width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.5)',
                    }}>
                        <CircularProgress size={60} thickness={5} />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default DeliveryBoyMap;
